import { useLocation, useRouter } from "@tanstack/react-router";
import React, { createContext, useContext } from "react";
import { useAuth } from "./AuthContext";

interface NavigationContextValue {
    // Navigation methods
    navigateToHome: () => void;
    navigateToStoryMode: () => void;
    navigateToMyStories: () => void;
    navigateToSettings: () => void;
    navigateToStory: (storyId: string) => void;
    navigateToSignIn: (redirectTo?: string) => void;
    navigateToSignUp: (redirectTo?: string) => void;
    navigateBack: () => void;

    // Current route information
    currentPath: string;
    isOnAuthPage: boolean;
    isOnStoryPage: boolean;

    // Auth-aware navigation helpers
    requireAuth: (callback: () => void, redirectTo?: string) => void;
    canAccessRoute: (route: string) => boolean;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export function NavigationProvider({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const location = useLocation();
    const { user } = useAuth();

    // Navigation methods
    const navigateToHome = () => {
        router.navigate({ to: "/" });
    };

    const navigateToStoryMode = () => {
        if (!user) {
            navigateToSignIn("/story-mode");
            return;
        }
        router.navigate({ to: "/story-mode" });
    };

    const navigateToMyStories = () => {
        if (!user) {
            navigateToSignIn("/my-stories");
            return;
        }
        router.navigate({ to: "/my-stories" });
    };

    const navigateToSettings = () => {
        if (!user) {
            navigateToSignIn("/settings");
            return;
        }
        router.navigate({ to: "/settings" });
    };

    const navigateToStory = (storyId: string) => {
        if (!user) {
            navigateToSignIn(`/story/${storyId}`);
            return;
        }
        router.navigate({
            to: "/story/$storyId",
            params: { storyId },
        });
    };

    const navigateToSignIn = (redirectTo?: string) => {
        const searchParams = redirectTo ? { redirectTo } : {};
        router.navigate({
            to: "/sign-in",
            search: searchParams,
        });
    };

    const navigateToSignUp = (redirectTo?: string) => {
        const searchParams = redirectTo ? { redirectTo } : {};
        router.navigate({
            to: "/sign-up",
            search: searchParams,
        });
    };

    const navigateBack = () => {
        router.history.back();
    };

    // Current route information
    const currentPath = location.pathname;
    const isOnAuthPage = currentPath.includes("/sign-in") || currentPath.includes("/sign-up");
    const isOnStoryPage = currentPath.startsWith("/story");

    // Auth-aware navigation helpers
    const requireAuth = (callback: () => void, redirectTo?: string) => {
        if (!user) {
            navigateToSignIn(redirectTo || currentPath);
            return;
        }
        callback();
    };

    const canAccessRoute = (route: string) => {
        const authRequiredRoutes = ["/story-mode", "/my-stories", "/settings"];
        const isAuthRequired = authRequiredRoutes.some(
            (authRoute) => route === authRoute || route.startsWith("/story/")
        );

        if (isAuthRequired && !user) {
            return false;
        }

        return true;
    };

    const value: NavigationContextValue = {
        // Navigation methods
        navigateToHome,
        navigateToStoryMode,
        navigateToMyStories,
        navigateToSettings,
        navigateToStory,
        navigateToSignIn,
        navigateToSignUp,
        navigateBack,

        // Current route information
        currentPath,
        isOnAuthPage,
        isOnStoryPage,

        // Auth-aware navigation helpers
        requireAuth,
        canAccessRoute,
    };

    return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation() {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error("useNavigation must be used within a NavigationProvider");
    }
    return context;
}
